import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dropdown,
  Input as AntInput,
  InputNumber,
  Select as AntSelect,
} from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { get, keyBy } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { MdErrorOutline } from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import { withRequesterVariables } from "./catalog";
import ConditionListEditor from "./ConditionListEditor";
import {
  addExit,
  addRule,
  conditionParts,
  DELAY_UNITS,
  delayBlocker,
  describeRules,
  EXIT_TYPES,
  exitsOf,
  exitType,
  moveRule,
  moveRuleTo,
  removeEdge,
  RULE_MODES,
  splitDelay,
  toSeconds,
  updateEdge,
  updateRuleConditions,
} from "./next-steps";
import { ICON_PATHS } from "./step-look";

const ADDERS = {
  RULE: "RULE",
  TIMER: EXIT_TYPES.TIMER,
  EVENT: EXIT_TYPES.EVENT,
};

const EXIT_ICONS = {
  [EXIT_TYPES.BACK]: ICON_PATHS.back,
  [EXIT_TYPES.EVENT]: ICON_PATHS.bolt,
  [EXIT_TYPES.TIMER]: ICON_PATHS.clock,
};

const RULE_ACTIONS = {
  CONDITIONS: "CONDITIONS",
  UP: "UP",
  DOWN: "DOWN",
  DELETE: "DELETE",
};

const EXIT_ACTIONS = {
  EDIT: "EDIT",
  DELETE: "DELETE",
};

const NextSteps = ({
  node,
  nodes,
  edges,
  variables,
  focusedEdgeId,
  onEdgesChange,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const NEXT = t("CANVAS_EDITOR.NEXT", { returnObjects: true });
  const UNNAMED = t("CANVAS_EDITOR.STEPS.UNNAMED");
  const CONDITION_LABELS = t("FORM.POSSIBLE_STATUSES.CONDITIONS", {
    returnObjects: true,
  });
  const conditionVariables = useMemo(
    () =>
      withRequesterVariables(
        variables,
        t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }),
      ),
    [variables, t],
  );
  const [openConditions, setOpenConditions] = useState({});
  const [editingExits, setEditingExits] = useState({});
  const [adding, setAdding] = useState(null);
  const [dragged, setDragged] = useState(null);
  const cardRefs = useRef({});

  const source = node.id;
  const rules = describeRules(edges, source);
  const exits = exitsOf(edges, source);
  const timers = exits.filter((edge) => exitType(edge) === EXIT_TYPES.TIMER);
  const blocker = delayBlocker(node, nodes, edges);
  const nodeById = keyBy(nodes, "id");
  const nameOf = (id) => get(nodeById, [id, "data", "name"]) || UNNAMED;
  const blockerText = (group) =>
    blocker
      ? t(`CANVAS_EDITOR.NEXT.${group}.${blocker.reason}`, {
          rank: blocker.rank,
        })
      : null;
  const targetOptions = nodes
    .filter((item) => item.id !== source)
    .map((item) => ({ value: item.id, label: item.data.name || UNNAMED }));
  const unitOptions = Object.values(DELAY_UNITS).map((unit) => ({
    value: unit,
    label: NEXT.UNITS[unit],
  }));

  useEffect(() => {
    if (!focusedEdgeId) return;
    const card = cardRefs.current[focusedEdgeId];
    if (card && card.scrollIntoView) card.scrollIntoView({ block: "nearest" });
    setOpenConditions((current) => ({ ...current, [focusedEdgeId]: true }));
  }, [focusedEdgeId]);

  const change = (updater) => onEdgesChange((current) => updater(current));

  const toggle = (setter, edgeId) =>
    setter((current) => ({ ...current, [edgeId]: !current[edgeId] }));

  const create = (target) => {
    const type = adding;
    setAdding(null);
    if (!target) return;
    const id = cuid();
    if (type !== ADDERS.RULE) {
      change((current) => addExit(current, { source, target, type, id }));
      setEditingExits((open) => ({ ...open, [id]: true }));
      return;
    }
    change((current) => addRule(current, { source, target, id }));
    setOpenConditions((open) => ({ ...open, [id]: true }));
  };

  const onRuleAction = (edge, key) => {
    if (key === RULE_ACTIONS.CONDITIONS)
      return toggle(setOpenConditions, edge.id);
    if (key === RULE_ACTIONS.UP)
      return change((current) => moveRule(current, source, edge.id, -1));
    if (key === RULE_ACTIONS.DOWN)
      return change((current) => moveRule(current, source, edge.id, 1));
    return change((current) => removeEdge(current, edge.id));
  };

  const onExitAction = (edge, key) => {
    if (key === EXIT_ACTIONS.EDIT) return toggle(setEditingExits, edge.id);
    return change((current) => removeEdge(current, edge.id));
  };

  const dropOn = (index) => {
    if (!dragged) return;
    change((current) => moveRuleTo(current, source, dragged, index));
    setDragged(null);
  };

  const targetSelect = (edge) => (
    <AntSelect
      showSearch
      virtual={false}
      className="canvas-next__target"
      optionFilterProp="label"
      aria-label={NEXT.GO_TO}
      placeholder={NEXT.PICK_STEP}
      options={targetOptions}
      value={edge.target}
      onChange={(target) =>
        change((current) => updateEdge(current, edge.id, { target }))
      }
    />
  );

  const sentence = (rule) => {
    if (rule.mode === RULE_MODES.OTHERWISE)
      return (
        <>
          <strong>{NEXT.OTHERWISE}</strong>
          <span className="canvas-next__muted"> — {NEXT.OTHERWISE_HINT}</span>
        </>
      );
    if (rule.mode === RULE_MODES.ALWAYS)
      return (
        <>
          <strong>{NEXT.ALWAYS}</strong>
          <span className="canvas-next__muted"> — {NEXT.ALWAYS_HINT}</span>
        </>
      );
    const conditions = get(rule.edge, "data.conditions") || [];
    return (
      <>
        <strong>{NEXT.IF}</strong>
        {conditions.map((condition, index) => {
          const parts = conditionParts(
            condition,
            conditionVariables,
            CONDITION_LABELS,
          );
          return (
            <span key={condition.id || index} className="canvas-next__clause">
              {index > 0 ? (
                <span className="canvas-next__muted">{NEXT.AND}</span>
              ) : null}
              <span className="canvas-next__chip">
                {parts.field || NEXT.NO_FIELD}
              </span>
              <span className="canvas-next__muted">{parts.operation}</span>
              {parts.value !== null ? (
                <span className="canvas-next__chip">{parts.value}</span>
              ) : null}
            </span>
          );
        })}
      </>
    );
  };

  const targetPicker = () => (
    <AntSelect
      autoFocus
      defaultOpen
      showSearch
      virtual={false}
      className="canvas-next__target"
      optionFilterProp="label"
      placeholder={NEXT.PICK_STEP}
      aria-label={NEXT.PICK_STEP}
      options={targetOptions}
      onChange={create}
      onBlur={() => setAdding(null)}
    />
  );

  const exitSentence = (edge) => {
    const type = exitType(edge);
    if (type === EXIT_TYPES.BACK) return NEXT.BACK;
    if (type === EXIT_TYPES.EVENT)
      return `${NEXT.EVENT} « ${get(edge, "data.event") || "?"} »`;
    const delay = splitDelay(get(edge, "data.delaySeconds"));
    return `${NEXT.TIMER} ${delay.value} ${NEXT.UNITS[delay.unit]} ${NEXT.TIMER_SUFFIX}`;
  };

  const exitEditor = (edge) => {
    const type = exitType(edge);
    const delay = splitDelay(get(edge, "data.delaySeconds"));
    return (
      <div className="canvas-next__exit-editor">
        {type === EXIT_TYPES.EVENT ? (
          <AntInput
            className="canvas-next__event"
            placeholder={NEXT.EVENT_PLACEHOLDER}
            aria-label={NEXT.EVENT_PLACEHOLDER}
            value={get(edge, "data.event") || ""}
            onChange={(event) =>
              change((current) =>
                updateEdge(current, edge.id, { event: event.target.value }),
              )
            }
          />
        ) : null}
        {type === EXIT_TYPES.TIMER ? (
          <div className="canvas-next__delay-row">
            <InputNumber
              min={1}
              precision={0}
              className="canvas-next__delay"
              aria-label={NEXT.TIMER}
              value={delay.value || null}
              onChange={(value) =>
                change((current) =>
                  updateEdge(current, edge.id, {
                    delaySeconds: toSeconds(value, delay.unit),
                  }),
                )
              }
            />
            <AntSelect
              className="canvas-next__unit"
              aria-label={NEXT.UNIT}
              options={unitOptions}
              value={delay.unit}
              onChange={(unit) =>
                change((current) =>
                  updateEdge(current, edge.id, {
                    delaySeconds: toSeconds(delay.value || 1, unit),
                  }),
                )
              }
            />
          </div>
        ) : null}
        <div className="canvas-next__goto">
          <CanvasIcon path={ICON_PATHS.arrow} size={16} strokeWidth={2} />
          {targetSelect(edge)}
        </div>
      </div>
    );
  };

  const moreButton = (label, items, onClick) => (
    <Dropdown trigger={["click"]} menu={{ items, onClick }}>
      <button type="button" className="canvas-next__more" aria-label={label}>
        <CanvasIcon path={ICON_PATHS.dots} size={18} strokeWidth={3} />
      </button>
    </Dropdown>
  );

  return (
    <div className="canvas-next">
      <section className="canvas-next__section">
        <div>
          <h3 className="canvas-next__title">{NEXT.TITLE}</h3>
          <p className="canvas-next__hint">{NEXT.HINT}</p>
        </div>
        {rules.length === 0 ? (
          <div className="canvas-next__end">
            <CanvasIcon path={ICON_PATHS.flag} size={22} />
            <div className="canvas-next__end-text">
              <strong>{NEXT.NO_RULES_TITLE}</strong>
              <span>{NEXT.NO_RULES}</span>
              {adding === ADDERS.RULE ? null : (
                <button
                  type="button"
                  className="canvas-next__link"
                  onClick={() => setAdding(ADDERS.RULE)}
                >
                  {NEXT.ADD_FOLLOW_UP}
                </button>
              )}
            </div>
          </div>
        ) : null}
        {rules.map((rule, index) => {
          const { edge } = rule;
          const open = Boolean(openConditions[edge.id]);
          return (
            <div
              key={edge.id}
              ref={(element) => {
                cardRefs.current[edge.id] = element;
              }}
              className={classNames("canvas-next__rule", {
                "canvas-next__rule--focused": edge.id === focusedEdgeId,
                "canvas-next__rule--dragged": edge.id === dragged,
              })}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => dropOn(index)}
            >
              <span
                className="canvas-next__grip"
                aria-hidden="true"
                draggable
                onDragStart={() => setDragged(edge.id)}
                onDragEnd={() => setDragged(null)}
              >
                <CanvasIcon path={ICON_PATHS.grip} size={16} strokeWidth={3} />
              </span>
              <span className="canvas-next__rank">{rule.rank}</span>
              <div className="canvas-next__rule-body">
                <div className="canvas-next__sentence">{sentence(rule)}</div>
                <div className="canvas-next__goto">
                  <CanvasIcon
                    path={ICON_PATHS.arrow}
                    size={16}
                    strokeWidth={2}
                  />
                  {targetSelect(edge)}
                </div>
                {rule.shadowedBy ? (
                  <div className="canvas-next__warning">
                    <MdErrorOutline size={16} aria-hidden="true" />
                    {t("CANVAS_EDITOR.NEXT.UNREACHABLE", {
                      rank: rule.shadowedBy,
                    })}
                  </div>
                ) : null}
                {rule.incomplete ? (
                  <div className="canvas-next__warning">
                    <MdErrorOutline size={16} aria-hidden="true" />
                    {NEXT.INCOMPLETE}
                  </div>
                ) : null}
                {open ? (
                  <ConditionListEditor
                    conditions={get(edge, "data.conditions") || []}
                    variables={conditionVariables}
                    labels={CONDITION_LABELS}
                    onChange={(conditions) =>
                      change((current) =>
                        updateRuleConditions(current, edge.id, conditions),
                      )
                    }
                  />
                ) : null}
              </div>
              {moreButton(
                NEXT.RULE_ACTIONS,
                [
                  {
                    key: RULE_ACTIONS.CONDITIONS,
                    label: open ? NEXT.HIDE_CONDITIONS : NEXT.EDIT_CONDITIONS,
                  },
                  {
                    key: RULE_ACTIONS.UP,
                    label: NEXT.MOVE_UP,
                    disabled: index === 0,
                  },
                  {
                    key: RULE_ACTIONS.DOWN,
                    label: NEXT.MOVE_DOWN,
                    disabled: index === rules.length - 1,
                  },
                  {
                    key: RULE_ACTIONS.DELETE,
                    label: NEXT.DELETE,
                    danger: true,
                  },
                ],
                ({ key }) => onRuleAction(edge, key),
              )}
            </div>
          );
        })}
        {adding === ADDERS.RULE ? (
          targetPicker()
        ) : rules.length ? (
          <button
            type="button"
            className="canvas-dashed-button"
            onClick={() => setAdding(ADDERS.RULE)}
          >
            <CanvasIcon path={ICON_PATHS.plus} size={16} strokeWidth={2.2} />
            {NEXT.ADD_RULE}
          </button>
        ) : null}
      </section>

      <section className="canvas-next__section canvas-next__section--tight">
        <h4 className="canvas-next__subtitle">{NEXT.OTHER_EXITS}</h4>
        {exits.length === 0 ? (
          <p className="canvas-next__muted canvas-next__note">
            {NEXT.NO_OTHER_EXITS}
          </p>
        ) : null}
        {exits.map((edge) => {
          const type = exitType(edge);
          const editing = Boolean(editingExits[edge.id]);
          return (
            <div
              key={edge.id}
              ref={(element) => {
                cardRefs.current[edge.id] = element;
              }}
              className={classNames("canvas-next__exit", {
                "canvas-next__rule--focused": edge.id === focusedEdgeId,
              })}
            >
              <div className="canvas-next__exit-head">
                <span
                  className={`canvas-next__exit-icon canvas-next__exit-icon--${type.toLowerCase()}`}
                >
                  <CanvasIcon
                    path={EXIT_ICONS[type]}
                    size={16}
                    strokeWidth={2}
                  />
                </span>
                <span className="canvas-next__exit-text">
                  {exitSentence(edge)}{" "}
                  <span className="canvas-next__muted">→</span>{" "}
                  <strong>{nameOf(edge.target)}</strong>
                </span>
                {type === EXIT_TYPES.BACK ? (
                  <span className="canvas-next__auto">{NEXT.AUTOMATIC}</span>
                ) : null}
                {moreButton(
                  NEXT.EXIT_ACTIONS,
                  [
                    {
                      key: EXIT_ACTIONS.EDIT,
                      label: editing ? NEXT.CLOSE_EDIT : NEXT.EDIT,
                    },
                    {
                      key: EXIT_ACTIONS.DELETE,
                      label: NEXT.DELETE,
                      danger: true,
                    },
                  ],
                  ({ key }) => onExitAction(edge, key),
                )}
              </div>
              {editing ? exitEditor(edge) : null}
              {type === EXIT_TYPES.TIMER && blocker ? (
                <div className="canvas-next__warning">
                  <MdErrorOutline size={16} aria-hidden="true" />
                  {blockerText("TIMER_BLOCKED")}
                </div>
              ) : null}
            </div>
          );
        })}
        {timers.length > 1 ? (
          <div className="canvas-next__warning">
            <MdErrorOutline size={16} aria-hidden="true" />
            {NEXT.TIMER_SHORTEST}
          </div>
        ) : null}
        {adding === ADDERS.TIMER || adding === ADDERS.EVENT ? (
          targetPicker()
        ) : (
          <div className="canvas-next__adders">
            {blocker ? null : (
              <button
                type="button"
                className="canvas-small-button"
                onClick={() => setAdding(ADDERS.TIMER)}
              >
                <CanvasIcon
                  path={ICON_PATHS.clock}
                  size={15}
                  strokeWidth={2}
                  className="canvas-small-button__timer"
                />
                {NEXT.ADD_TIMER}
              </button>
            )}
            <button
              type="button"
              className="canvas-small-button"
              onClick={() => setAdding(ADDERS.EVENT)}
            >
              <CanvasIcon
                path={ICON_PATHS.bolt}
                size={15}
                strokeWidth={2}
                className="canvas-small-button__event"
              />
              {NEXT.ADD_EVENT}
            </button>
          </div>
        )}
        {blocker ? (
          <p className="canvas-next__muted canvas-next__note">
            {blockerText("NO_TIMER")}
          </p>
        ) : null}
      </section>
    </div>
  );
};

NextSteps.propTypes = {
  node: PropTypes.object.isRequired,
  nodes: PropTypes.arrayOf(PropTypes.object),
  edges: PropTypes.arrayOf(PropTypes.object),
  variables: PropTypes.array,
  focusedEdgeId: PropTypes.string,
  onEdgesChange: PropTypes.func.isRequired,
};

NextSteps.defaultProps = {
  nodes: [],
  edges: [],
  variables: [],
  focusedEdgeId: null,
};

export default NextSteps;
